import { NavLink, useLocation } from "react-router-dom";
import { useAuth, roleLabel } from "@/context/AuthContext";
import { isEffectiveRole } from "@/components/ProtectedRoute";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import {
  LayoutDashboard,
  Building2,
  Users,
  UserCircle,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ClipboardList,
  ListChecks,
  ClipboardCheck,
  TrendingUp,
  BedDouble,
  UsersRound,
  Settings2,
  Layout,
  FileText,
  AlertTriangle,
  BellRing,
  Download,
  BookOpen,
  Activity,
  Globe2,
  CalendarDays,
  Flag,
  HeartPulse,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, key: "dashboard", roles: "*", section: "Operatività" },
  { to: "/strutture", label: "Strutture", icon: Building2, key: "strutture", roles: "*", section: "Operatività" },
  { to: "/controlli", label: "Controlli", icon: ClipboardList, key: "controlli", roles: ["super_admin", "capo_area", "resident_manager", "housekeeping_manager", "front_office_manager", "read_only"], section: "Operatività" },
  { to: "/ispezioni", label: "Ispezioni", icon: ClipboardCheck, key: "audit", roles: ["super_admin", "capo_area", "read_only"], section: "Operatività" },
  { to: "/performance", label: "Performance", icon: TrendingUp, key: "performance", roles: ["super_admin", "capo_area", "resident_manager"], section: "Performance" },
  { to: "/housekeeping", label: "Housekeeping", icon: BedDouble, key: "housekeeping", roles: ["super_admin", "capo_area", "housekeeping_manager", "resident_manager"], section: "Performance" },
  { to: "/staff", label: "Staff", icon: UsersRound, key: "staff", roles: ["super_admin", "capo_area", "resident_manager", "housekeeping_manager", "front_office_manager", "read_only"], section: "Performance" },
  { to: "/performance/web-reputation", label: "Web Reputation", icon: Globe2, key: "web-reputation", roles: "*", section: "Performance" },
  { to: "/performance/report-settimanali", label: "Report Settimanali", icon: CalendarDays, key: "report-settimanali", roles: ["super_admin", "capo_area"], section: "Performance", flag: "report_settimanali" },
  { to: "/relazioni", label: "Relazioni", icon: FileText, key: "relazioni", roles: ["super_admin", "capo_area", "resident_manager"], section: "Comunicazione" },
  { to: "/alert", label: "Alert", icon: AlertTriangle, key: "alert", roles: ["super_admin", "capo_area", "resident_manager", "housekeeping_manager", "front_office_manager"], section: "Comunicazione" },
  { to: "/notifiche", label: "Notifiche", icon: BellRing, key: "notifiche", roles: "*", section: "Comunicazione" },
  { to: "/report", label: "Report", icon: Download, key: "report", roles: ["super_admin", "capo_area", "resident_manager", "housekeeping_manager", "front_office_manager", "read_only"], section: "Report" },
  { to: "/templates", label: "Templates", icon: ListChecks, key: "templates", roles: ["super_admin"], section: "Configurazione" },
  { to: "/configurazione/valutazioni", label: "Questionario", icon: Settings2, key: "config-valutazioni", roles: ["super_admin"], section: "Configurazione" },
  { to: "/configurazione/alert", label: "Regole alert", icon: BellRing, key: "config-alert", roles: ["super_admin"], section: "Configurazione" },
  { to: "/configurazione/audit-log", label: "Registro attività", icon: ListChecks, key: "audit-log", roles: ["super_admin"], section: "Configurazione" },
  { to: "/configurazione/ai", label: "AI Assistant", icon: Sparkles, key: "config-ai", roles: ["super_admin"], section: "Configurazione" },
  { to: "/configurazione/scheduler", label: "Scheduler", icon: Activity, key: "config-scheduler", roles: ["super_admin"], section: "Configurazione" },
  { to: "/configurazione/checklist-camera", label: "Checklist Camera", icon: BedDouble, key: "config-checklist-camera", roles: ["super_admin"], section: "Configurazione" },
  { to: "/configurazione/obiettivi-hotel", label: "Obiettivi Hotel", icon: Settings2, key: "config-obiettivi", roles: ["super_admin"], section: "Configurazione" },
  { to: "/configurazione/schema-relazioni", label: "Schema Relazioni", icon: Layout, key: "config-schema-relazioni", roles: ["super_admin"], section: "Configurazione" },
  { to: "/configurazione/demo-tools", label: "Demo & Reset", icon: Sparkles, key: "config-demo-tools", roles: ["super_admin"], section: "Configurazione" },
  { to: "/configurazione/feature-flags", label: "Feature Flags", icon: Flag, key: "config-feature-flags", roles: ["super_admin"], section: "Configurazione" },
  { to: "/admin/health", label: "Health Monitor", icon: HeartPulse, key: "admin-health", roles: ["super_admin"], section: "Configurazione" },
  { to: "/utenti", label: "Utenti", icon: Users, key: "utenti", roles: ["super_admin"], section: "Configurazione" },
  { to: "/guida", label: "Guida", icon: BookOpen, key: "guida", roles: "*", section: "Account" },
  { to: "/installa", label: "Installa app", icon: Download, key: "installa", roles: "*", section: "Account" },
  { to: "/profilo", label: "Profilo", icon: UserCircle, key: "profilo", roles: "*", section: "Account" },
];

const SECTION_ORDER = ["Operatività", "Performance", "Comunicazione", "Report", "Configurazione", "Account"];

export function SidebarNav({ collapsed = false, onItemClick }) {
  const { user } = useAuth();
  const location = useLocation();
  const { flags } = useFeatureFlags();
  const visible = NAV_ITEMS.filter((it) => {
    const roleOk = it.roles === "*" || isEffectiveRole(user, it.roles);
    if (!roleOk) return false;
    if (it.flag && flags[it.flag] !== true) return false;
    return true;
  });
  return (
    <nav className={cn("flex-1 px-3 py-5 space-y-1 overflow-y-auto", collapsed && "px-2")}>
      {SECTION_ORDER.map((sec) => {
        const itemsInSec = visible.filter((i) => i.section === sec);
        if (itemsInSec.length === 0) return null;
        return (
          <div key={sec} className="mb-3">
            {!collapsed && (
              <div className="px-3 py-1.5 text-[9px] uppercase tracking-[0.22em] text-muted-foreground/70 font-semibold">{sec}</div>
            )}
            {itemsInSec.map((item) => {
              const Icon = item.icon;
              const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  onClick={onItemClick}
                  data-testid={`nav-${item.key}`}
                  className={cn("ih-nav-item", active && "active", collapsed && "justify-center px-2")}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className="h-[18px] w-[18px] shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}

export function SidebarBrand({ collapsed }) {
  return (
    <div className={cn("flex items-center gap-2.5 px-5 h-[68px] border-b border-border/50", collapsed && "justify-center px-0")} data-testid="sidebar-brand">
      <img
        src="/icon-192.png?v=107"
        alt="IH AuditFlow"
        className="app-logo w-9 h-9 shrink-0 object-contain rounded-lg"
      />
      {!collapsed && (
        <span
          className="font-display font-semibold text-[17px] tracking-tight text-foreground whitespace-nowrap"
          data-testid="brand-wordmark"
        >
          IH AuditFlow
        </span>
      )}
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const location = useLocation();
  const { flags } = useFeatureFlags();

  const visible = NAV_ITEMS.filter((it) => {
    const roleOk = it.roles === "*" || isEffectiveRole(user, it.roles);
    if (!roleOk) return false;
    if (it.flag && flags[it.flag] !== true) return false;
    return true;
  });

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "fixed top-0 left-0 h-full z-40 border-r border-border/60 ih-glass-strong transition-[width] duration-300 ease-out flex-col hidden md:flex",
        collapsed ? "w-[80px]" : "w-[260px]"
      )}
    >
      {/* Brand */}
      <SidebarBrand collapsed={collapsed} />

      {/* Nav */}
      <nav className={cn("flex-1 px-3 py-5 space-y-1 overflow-y-auto", collapsed && "px-2")}>
        {SECTION_ORDER.map((sec) => {
          const itemsInSec = visible.filter((i) => i.section === sec);
          if (itemsInSec.length === 0) return null;
          return (
            <div key={sec} className="mb-3">
              {!collapsed && (
                <div className="px-3 py-1.5 text-[9px] uppercase tracking-[0.22em] text-muted-foreground/70 font-semibold">{sec}</div>
              )}
              {itemsInSec.map((item) => {
                const Icon = item.icon;
                const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
                return (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    data-testid={`nav-${item.key}`}
                    className={cn("ih-nav-item", active && "active", collapsed && "justify-center px-2")}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User block */}
      <div className={cn("p-3 border-t border-border/50", collapsed && "px-2")}>
        {!collapsed ? (
          <div className="rounded-xl bg-accent/40 p-3 flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Connesso come</span>
            <span className="text-sm font-medium leading-tight truncate">{user?.nome} {user?.cognome}</span>
            <span className="text-xs text-muted-foreground">{roleLabel(user?.role)}</span>
          </div>
        ) : (
          <div className="h-10 w-full rounded-xl bg-accent/40 flex items-center justify-center text-xs font-semibold">
            {user?.nome?.[0]}{user?.cognome?.[0]}
          </div>
        )}
        <button
          data-testid="sidebar-toggle"
          onClick={onToggle}
          className="mt-3 w-full flex items-center justify-center h-9 rounded-lg border border-border/60 hover:bg-accent/40 transition-colors"
          aria-label="Toggle sidebar"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
        {!collapsed && (
          <div className="mt-3 text-center text-[10px] tracking-wide text-muted-foreground/60" data-testid="ptk-credit">
            Dev by PTK Solutions ® 2026
          </div>
        )}
      </div>
    </aside>
  );
}
